import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { LogIn, Shield, Brain, TrendingUp, BarChart3, Bot, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const [companyId, setCompanyId] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(companyId, email, password);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Login failed';
      if (errorMessage.toLowerCase().includes('invalid') || errorMessage.toLowerCase().includes('incorrect')) {
        toast.error('Invalid username or password. Please try again.');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Brain, title: '10 AI Agents', desc: 'Intelligent retail analysis' },
    { icon: BarChart3, title: 'Real-time KPIs', desc: 'Business health monitoring' },
    { icon: Bot, title: 'ReasonBot', desc: 'Multilingual voice assistant' },
    { icon: TrendingUp, title: 'Smart Forecasts', desc: 'Demand prediction engine' },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Hero Section with Indian Retail Image */}
      <div className="hidden lg:flex lg:w-3/5 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/3be8241e-c53f-484d-98e6-d65677792fa0/images/c4ea00c857a605436f2335bd67f092d63b9476dac9731886b008c443e0f9c166.png)',
          }}
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/60" />
        
        {/* Content on left panel */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Top - Brand */}
          <div>
            <div className="flex items-center gap-4 mb-8">
              <img 
                src="/reason-logo.jpg" 
                alt="REASON" 
                className="h-16 w-auto object-contain rounded-lg shadow-2xl"
              />
              <div>
                <h1 className="text-4xl font-bold text-white tracking-tight">
                  Reason <span className="text-blue-400">RetailIQ</span>
                </h1>
                <p className="text-slate-300 text-sm mt-1">Enterprise Retail Intelligence Platform</p>
              </div>
            </div>
            
            {/* Tagline */}
            <div className="mt-8 max-w-lg">
              <h2 className="text-3xl font-semibold text-white leading-tight mb-4">
                Transform Your Retail Business with <span className="text-blue-400">AI-Powered Intelligence</span>
              </h2>
              <p className="text-slate-300 text-lg leading-relaxed">
                Harness the power of 10 specialized AI agents to optimize inventory, boost sales, 
                and drive profitability across your retail operations.
              </p>
            </div>
          </div>

          {/* Middle - Features */}
          <div className="grid grid-cols-2 gap-4 my-8">
            {features.map((feature, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-3 p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all"
              >
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <feature.icon className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">{feature.title}</p>
                  <p className="text-slate-400 text-xs">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom - Trust Badges */}
          <div className="space-y-4">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2 text-slate-400">
                <Shield className="h-4 w-4 text-green-400" />
                <span className="text-xs">Enterprise Security</span>
              </div>
              <div className="flex items-center gap-2 text-slate-400">
                <Sparkles className="h-4 w-4 text-yellow-400" />
                <span className="text-xs">AI Powered</span>
              </div>
              <div className="flex items-center gap-2 text-slate-400">
                <Bot className="h-4 w-4 text-blue-400" />
                <span className="text-xs">16 Languages</span>
              </div>
            </div>
            
            {/* Made in India Badge */}
            <div className="flex items-center gap-3 pt-4 border-t border-white/10">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-orange-500/20 via-white/10 to-green-500/20 border border-white/20">
                <span className="text-xs font-medium text-white">🇮🇳 Made in India</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20">
                <span className="text-xs font-medium text-blue-300">Startup India | DPIIT Recognized</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-2/5 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo - Only shown on small screens */}
          <div className="lg:hidden text-center mb-8">
            <div className="flex justify-center mb-4">
              <img 
                src="/reason-logo.jpg" 
                alt="REASON" 
                className="h-20 w-auto object-contain rounded-lg"
                data-testid="login-logo"
              />
            </div>
            <h1 className="text-3xl font-bold text-white mb-1" data-testid="login-title">
              Reason <span className="text-blue-400">RetailIQ</span>
            </h1>
            <p className="text-slate-400 text-sm">Enterprise Retail Intelligence Platform</p>
          </div>

          {/* Welcome Text - Desktop */}
          <div className="hidden lg:block mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Welcome Back</h2>
            <p className="text-slate-400">Sign in to access your retail intelligence dashboard</p>
          </div>

          {/* Login Card */}
          <Card className="shadow-2xl border border-slate-700 bg-slate-800/50 backdrop-blur-sm" data-testid="login-card">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-xl text-white flex items-center gap-2">
                <LogIn className="h-5 w-5 text-blue-400" />
                Sign In
              </CardTitle>
              <CardDescription className="text-slate-400">
                Enter your credentials to access the platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="companyId" className="text-slate-300 text-sm font-medium">
                    Company ID
                  </Label>
                  <Input
                    id="companyId"
                    data-testid="login-companyid-input"
                    type="text"
                    placeholder="e.g., REASON001"
                    value={companyId}
                    onChange={(e) => setCompanyId(e.target.value)}
                    required
                    className="h-12 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300 text-sm font-medium">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    data-testid="login-email-input"
                    type="email"
                    placeholder="user@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-300 text-sm font-medium">
                    Password
                  </Label>
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <Button
                  data-testid="login-submit-button"
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-semibold text-base transition-all duration-200 shadow-lg shadow-blue-600/25"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Signing in...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <LogIn className="h-5 w-5" />
                      <span>Sign In</span>
                    </div>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="text-center mt-8 space-y-3">
            <div className="flex items-center justify-center gap-2 text-slate-500 text-xs">
              <Shield className="h-3.5 w-3.5 text-green-500" />
              <span>Secured by enterprise-grade encryption</span>
            </div>
            
            {/* Made in India - Mobile */}
            <div className="lg:hidden flex items-center justify-center gap-2">
              <span className="text-xs text-slate-400">🇮🇳 Made in India | Startup India | DPIIT</span>
            </div>
            
            <div className="pt-2 border-t border-slate-700/50">
              <p className="text-slate-500 text-xs">
                ©2025 Reason Analytics Softech Pvt Ltd.
              </p>
              <p className="text-slate-600 text-xs mt-1">
                Reason RetailIQ v2.0
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
